/**
 * Address / venue-name normaliser — T1.1c (docs/06-tasks.md T1.1c, docs/05-dev-spec.md §7,
 * docs/02-data-profiles.md §10.3 «Нормализация адресов: конкретные примеры» + §11 takeaways).
 *
 * Framework-free TypeScript: no runtime dependency, pure string in → string out, safe to
 * call from ingest (T1.1b), the D2 matcher and the D1 seed CLI.
 *
 * WHY (docs/02 §10.3 — the same venue is written differently inside ONE export and across
 * files): «115569, ул.Шипиловский, д.37 корпус 2, Москва» (ЕПГУ 8.09) vs
 * «115569, ул.Шипиловский, д.37, к.2, Москва» (ЕВИ) vs «…д.37,к.2,…» (ЕВИ, другая строка)
 * vs «…, Москва, Москва» (Квоты, город задублирован). 02 §10.1: адрес совпадает только
 * в 31 из 48 пар до нормализации (36 после «NFKC + дефисы + lower + пробелы»), поэтому
 * адрес — не строковый ключ, а атрибут с нормализацией.
 *
 * CONTRACT (read before wiring a caller)
 *
 * 1. TOTAL: `normalizeAddress` / `normalizeVenueName` never throw, never return leading or
 *    trailing whitespace; `''` and non-string input → `''`.
 * 2. IDEMPOTENT for EVERY string: `normalize(normalize(x)) === normalize(x)` — asserted as a
 *    property over the docs/02 corpus plus 20 deterministic random seeds (`cases.ts`).
 *    Two consequences worth calling out:
 *      - the trailing parenthetical is stripped one group per iteration, so «X (a)» → «x»
 *        and «X (a) (b)» → «x». The task text names "strip ONE trailing parenthetical
 *        group"; the loop is what makes the property unconditional, and every string in the
 *        docs/02 corpus carries at most one trailing group, so on that corpus both readings
 *        produce identical results;
 *      - the structural block iterates to a bounded fixed point, because one pass can
 *        create work for an earlier one («к.,125319» → segments rejoined to «к. 125319» →
 *        tightened to «к.125319»).
 * 3. DETERMINISTIC + locale-pinned: case folding is `toLocaleLowerCase('ru')`, so
 *    «Татарстан» / «татарстан» / «ТАТАРСТАН» fold the same way on every host (the three
 *    register variants of docs/02 §10.3.3 — 29 + 170 + N occurrences — become one string).
 * 4. NFKC alone does NOT fix the hyphen trap: U+2011 (неразрывный дефис, docs/02 §10.3.6,
 *    `ул.1‑я Аэропортовская`, `ул.Горно‑алтайская`) decomposes to U+2010 under NFKC, not to
 *    ASCII `-` — the strings stay byte-different and `lower()`-different. The explicit
 *    HYPHEN_LIKE map below finishes the job.
 *    NFKC also has a VISIBLE but consistent side effect on the other docs/02 §10.5 trap:
 *    «№» (U+2116) compatibility-decomposes to "No" → "no", so «Школа № 878» folds to
 *    «школа no 878». Both sides of every comparison go through the same pipeline, so the
 *    key is stable; only the key's spelling looks odd.
 * 5. Output is a MATCH KEY, not a display address: lowercase, SPACE-joined segments (commas
 *    are punctuation variance: «д.37, к.2» == «д.37 корпус 2», docs/02 §10.3.1), `корпус N`
 *    folded, «г.» city prefix dropped, repeated segments removed, trailing parenthetical
 *    removed, edge punctuation trimmed. Keep the raw string in its own column for humans
 *    (05 §3 models `Venue.normalizedAddress` next to the master name).
 *
 * Rules and their evidence (docs/02 §10.3 numbering is cited in the tests):
 *   NFKC + hyphen unify    §10.3.6, §10.5 (U+2011, «» , №)
 *   RU case fold           §10.3.2 (Аэропортовская/аэропортовская), §10.3.3 ( registers)
 *   whitespace collapse    §10.3.1 (пропущен пробел после запятой), §10.5
 *   корпус fold            §10.3.1 (корпус 2 / к.2), §11 (токенизация корпуса/литеры)
 *   «г.» prefix drop       §10.3.5 (г. Москва vs Москва — 41/71 пересечение регионов)
 *   trailing paren strip   §10.3.3 (Республика татарстан (татарстан) ×3 регистров)
 *   repeated segment dedupe §10.3.1 (Москва, Москва)
 *   edge punctuation trim  §2 (`…«Школа № 878».` — имя площадки с точкой на конце)
 *   `ё → е`                the same fold `fio.ts` makes for a person's name («Пугачёва» /
 *                          «Пугачева» is one street). T.Y19, live-tested on prod copy.
 *   «им.» / «им» → «имени» the named-after particle, T.Y19: the ЕВИ sheet writes
 *                          «ул.им Е.И.Пугачёва» where the venue dictionary writes
 *                          «ул.имени Е.И.Пугачёва» — before this fold the two spellings
 *                          could not meet in ANY tier (the EXACT keys differ, the FUZZY
 *                          score loses the particle token, and the street guard compares
 *                          «им …» against «имени …» and refuses).
 */
/** Locale used for case folding. Pinned so results do not depend on the host ICU. */
export const RU_LOCALE = 'ru';
/** Safeguard for the parenthetical loop — no real address nests deeper than this. */
const MAX_PAREN_GROUPS = 8;
/**
 * Unicode hyphen/dash code points that must fold to ASCII `-`.
 * U+2011 is the documented trap; the rest are the neighbours that reach our data through
 * copy/paste from Google Sheets, Excel and PDF (docs/02 §10.5, docs/02b §4).
 */
// Exported since wave 9 (fio.ts): the ФИО fold reuses the same hyphen-family map so a
// copy/pasted U+2011 folds identically in an address key and in a person's name key.
export const HYPHEN_LIKE = /[\u00ad\u2010\u2011\u2012\u2013\u2014\u2015\u2212\ufe58\ufe63\uff0d]/g;
/** JS `\s` already covers NBSP + every Unicode space separator + U+FEFF. */
// Exported since wave 9 (fio.ts) — same rationale as HYPHEN_LIKE above.
export const WHITESPACE = /\s+/g;
/** Street/house/unit abbreviations that are written with and without a following space. */
const ABBREV_SPACE = /(^|[\s,;])((?:ул|улица|пер|переулок|пр|пр-т|просп|проспект|мкр|мкрн|бул|наб|ш|шоссе|пл|кв-л|обл|респ|пос|с|п|д|к|корп|стр|кв)\.)\s+/gu;
/**
 * The named-after particle «имени» and its two short spellings (T.Y19).
 * «ул.им Е.И.Пугачёва» (ЕВИ) and «ул.им.Е.И.Пугачёва» (Квоты) are one street with
 * «ул.имени Е.И.Пугачёва» (the venue dictionary); the fold also puts the SPACE back that
 * the dotted spelling ate, so «им.Е.И.Пугачёва» and «им Е.И.Пугачёва» land on one key.
 *
 * Order inside the alternation matters: «имени» must be tried before «им», or the bare
 * alternative would consume the «им» of «имени» and leave «ени» behind. The trailing
 * `\.?\s*` is consumed so the replacement is always «имени » — that is what makes the
 * dotted and the bare spelling converge. The lead class carries «.» as well as the
 * whitespace/comma set, because inside an address the particle follows the street
 * abbreviation's own dot («ул.им. …»).
 *
 * The lookahead is the word boundary, and it is what keeps the fold out of ordinary
 * words: «императорская» and «именитый» have «им»/«имени» followed by a LETTER, while the
 * particle is followed by a dot («им.Е.И.Пугачёва»), by whitespace or by the end. The
 * dot in «им.Е.» is consumed by `\.?` AFTER the lookahead has already seen it, so the
 * initials keep following the particle with no letter-to-letter seam to guard.
 */
const NAME_PARTICLE = /(^|[\s,;.])(?:имени|им)(?=[^\p{L}\p{N}]|$)(?:\.?\s*)/gu;
/**
 * A single-letter token of an address («е.», «и.» of «им.Е.И.Пугачёва») — an initial.
 * Digits are deliberately NOT initials: «ул.8 Марта» keeps its «8», or two different
 * numbered streets («8 Марта» / «9 Марта») would share a street key and the street
 * guard would go blind on exactly the pair it exists to separate.
 */
const INITIAL = /^\p{L}$/u;
/** «г.» city/region prefix — dropped entirely (docs/02 §10.3.5). */
const CITY_PREFIX = /(^|[\s,;])г\.(?=\s*[^,;\s])/gu;
/**
 * корпус variants → `корпус N` (docs/02 §10.3.1).
 * Matches `корпус 2`, `корпус-2`, `корп. 2`, `корп.2`, `к.2`, `к. 2`, `к2`, `К2`, `12к2`
 * (Cyrillic к and Latin k). The digit boundary is mandatory: «ул.Кирова», «мкр.Красная»,
 * «кв.15» (квартира) must not move. Note `\b` is unusable here — JS `\b` is ASCII-only,
 * so Cyrillic neighbours are spelled out explicitly.
 */
const KORPUS = /(^|[^a-zа-яё])(?:корпус|корп|k|к)[.\s-]*(\d{1,3}[a-zа-яё]?)(?=$|[^a-zа-яё0-9])/gu;
/** One trailing parenthetical group with no nesting inside. */
const TRAILING_PAREN = /\([^()]*\)\s*$/;
/** Postal code = exactly six digits at the very start (docs/02 §2 `Адрес` examples). */
const POSTAL = /^(\d{6})(?![0-9])/;
/** House number: the `д.N` token (house) — distinct from `к.N` (building) and `кв.N` (flat). */
const HOUSE = /(?:^|[\s,;])д\.\s*(\d{1,4}[a-zа-яё]?)/;
/**
 * Street-type prefixes that introduce the street segment of an address.
 *
 * LONGEST FIRST, on purpose (T.Y19): a regex alternation is first-match-wins, so with the
 * naive «ул|улица» order the bare spelling of a street type wins over the full one and
 * «улица имени Ленина» is sliced after «ул» — the street segment becomes «ица имени
 * ленина» and its key «ица имени» never meets the abbreviated «ул.Ленина» spelling.
 */
const STREET_PREFIX = '(?:улица|переулок|проспект|кв-л|просп|мкрн|пр-т|бул|наб|шоссе|ул|пер|мкр|пр|ш|пл)';
/**
 * Street segment: what follows a street-type prefix, up to the house token (`д.N`) or the
 * end of the string. The output of the normaliser is space-joined, so both `,` and space
 * act as separators on raw input.
 */
const STREET = new RegExp(`(?:^|[\\s,;])${STREET_PREFIX}\\.?\\s*([^,;]+?)(?=[\\s,]*д\\.\\s*\\d|$)`, 'u');
/** Token shape used by the fuzzy tier: letters/digits/hyphen runs, punctuation dropped. */
const TOKEN_SPLIT = /[^a-zа-яё0-9]+/u;
/** Bounded fixed-point budget — a real address converges in 2 iterations. */
const MAX_PASSES = 4;
/**
 * Normalises any RU address / venue-name string.
 * The shared pipeline; `normalizeAddress` and `normalizeVenueName` are the two named
 * entry points over it (kept as separate functions so D2 can diverge them later — e.g.
 * stripping «МБОУ СОШ»-style abbreviations from names — without touching the address path).
 *
 * The structural block is iterated to a BOUNDED fixed point. One pass can create work for
 * an earlier one: «к.,125319» survives the abbreviation pass (no space after the dot), then
 * `dedupeSegments` rejoins the segments into «к. 125319», which the abbreviation pass would
 * now tighten. Running the block until it stops changing is what makes the idempotency
 * property (contract §2) hold unconditionally rather than incidentally.
 */
export function normalizeText(raw, locale = RU_LOCALE) {
    if (typeof raw !== 'string')
        return '';
    let out = raw.normalize('NFKC');
    out = out.replace(HYPHEN_LIKE, '-');
    out = out.toLocaleLowerCase(locale);
    // `ё → е`, the same fold `fio.ts` applies to a person's name (T.Y19): «Пугачёва» and
    // «Пугачева» are one street and one school. The keys are match keys, not display text.
    out = out.replace(/ё/g, 'е');
    out = out.replace(WHITESPACE, ' ').trim();
    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const next = dropCityPrefix(out);
        const namedAfter = foldNameParticle(next);
        const tightened = tightenAbbreviations(namedAfter);
        const folded = foldKorpus(tightened);
        const unbracketed = stripTrailingParentheticals(folded);
        const deduped = dedupeSegments(unbracketed);
        const trimmed = trimEdgePunctuation(deduped);
        const settled = trimmed.replace(WHITESPACE, ' ').trim();
        if (settled === out)
            break;
        out = settled;
    }
    return out;
}
/** `normalizeAddress` — the match key for `Venue.normalizedAddress` / `Адрес` columns. */
export function normalizeAddress(raw) {
    return normalizeText(raw);
}
/** `normalizeVenueName` — the match key for `Venue.name` / `Площадка` columns. */
export function normalizeVenueName(raw) {
    return normalizeText(raw);
}
/** «г. Москва» → «москва». Never empties the string (guarded). */
function dropCityPrefix(value) {
    const next = value.replace(CITY_PREFIX, '$1');
    return next.trim().length === 0 ? value : next;
}
/**
 * «ул.им Е.И.Пугачёва» / «ул.им.Е.И.Пугачёва» → «ул.имени е.и.пугачёва» (T.Y19).
 * Runs inside the structural block (so the fixed point covers it) and is exported for
 * `streetKeyOf`, which also sees keys normalised by an OLDER build of this module.
 */
export function foldNameParticle(value) {
    const next = value.replace(NAME_PARTICLE, '$1имени ');
    return next.trim().length === 0 ? value : next;
}
/** «ул. Центральная» → «ул.центральная» — the space after an abbreviation is noise. */
function tightenAbbreviations(value) {
    const next = value.replace(ABBREV_SPACE, '$1$2');
    return next.trim().length === 0 ? value : next;
}
/** «д.37, к.2» → «д.37, корпус 2» (docs/02 §10.3.1); «д.12к2» → «д.12 корпус 2». */
function foldKorpus(value) {
    return value.replace(KORPUS, (_match, lead, num) => {
        // «12к2» attaches to a house number — keep a space between the two numbers.
        const separator = /\d/.test(lead) ? ' ' : '';
        return `${lead}${separator}корпус ${num}`;
    });
}
/**
 * Strips trailing parenthetical groups, one per iteration, until none is left.
 * «республика татарстан (татарстан)» → «республика татарстан» (docs/02 §10.3.3).
 * A paren-only string is left alone — the normaliser never returns '' from a non-empty input.
 */
function stripTrailingParentheticals(value) {
    let out = value.trim();
    for (let i = 0; i < MAX_PAREN_GROUPS; i += 1) {
        const match = TRAILING_PAREN.exec(out);
        if (!match)
            break;
        const head = out
            .slice(0, match.index)
            .trim()
            .replace(/[,;]$/, '')
            .trim();
        if (head.length === 0)
            break;
        out = head;
    }
    return out;
}
/**
 * Drops repeated comma-separated segments («Москва, Москва» → «Москва», docs/02 §10.3.1)
 * and empty ones («д.37,, Москва»). A segment is only dropped when an identical one is
 * already present, so «Долгое Ледово, Московская область» is untouched.
 *
 * The segments are re-joined with SPACES: the comma carries no information for matching and
 * its presence is pure variance — «д.37, к.2» (ЕВИ) and «д.37 корпус 2» (ЕПГУ 8.09) are the
 * same building (docs/02 §10.3.1), and a key that keeps the comma would keep them apart.
 */
function dedupeSegments(value) {
    const segments = value.split(',');
    const seen = new Set();
    const kept = [];
    for (const segment of segments) {
        const trimmed = segment.trim();
        if (trimmed.length === 0)
            continue;
        if (seen.has(trimmed))
            continue;
        seen.add(trimmed);
        kept.push(trimmed);
    }
    return kept.length === 0 ? value.trim() : kept.join(' ');
}
/** Trims punctuation/symbols (and the whitespace around them) from both ends. */
function trimEdgePunctuation(value) {
    return value.replace(/^[\p{P}\p{S}\s]+|[\p{P}\p{S}\s]+$/gu, '');
}
/** Leading 6-digit postal code of a (normalised) address, or null. */
export function postalCodeOf(normalizedAddress) {
    const match = POSTAL.exec(normalizedAddress);
    return match?.[1] ?? null;
}
/** House number of a (normalised) address — `д.37 корпус 2` → `37`, or null. */
export function houseNumberOf(normalizedAddress) {
    const match = HOUSE.exec(normalizedAddress);
    return match?.[1] ?? null;
}
/**
 * Street key of a (normalised) address: the two tokens that follow a street-type prefix.
 * `115569, ул.1-я аэропортовская, д.1, москва` → `1-я аэропортовская`.
 * Returns null when no street segment is identifiable (then the matcher's street guard
 * is skipped rather than guessed).
 *
 * Named-after streets (T.Y19): the particle is folded FIRST («им»/«им.» → «имени»), so a
 * key never spells it two ways, and single-letter initials are dropped BEFORE the two
 * tokens are taken — «ул.им Е.И.Пугачёва» and «ул.имени Е.И.Пугачёва» both key as
 * «имени пугачёва» instead of the never-meeting «им е» / «имени е». The initials carry no
 * discriminating power anyway (the matcher's token similarity already counts them as
 * noise), while keeping them would make the street guard refuse the very pair it should
 * let through. Digits survive: «ул.8 Марта» and «ул.9 Марта» must stay distinct.
 */
export function streetKeyOf(normalizedAddress) {
    const match = STREET.exec(foldNameParticle(normalizedAddress));
    const captured = match?.[1];
    if (captured === undefined)
        return null;
    const tokens = captured
        .trim()
        .split(/[\s.]+/u)
        .filter((token) => token.length > 0 && !INITIAL.test(token))
        .slice(0, 2);
    return tokens.length === 0 ? null : tokens.join(' ');
}
/** Splits a normalised string into comparison tokens (letters/digits, punctuation dropped). */
export function tokenize(normalized) {
    return normalized
        .split(TOKEN_SPLIT)
        .map((token) => token.trim())
        .filter((token) => token.length > 0);
}
//# sourceMappingURL=normalize.js.map